import {
  AlignmentDocumentControlNo,
  AmountInfoSettingType,
  CurrencyCode,
  DateOptionType,
  DocumentFontType,
  DocumentFormat,
  DocumentHeaderSize,
  ExpenseHeaderType,
  ExpenseType,
  HeaderOptionName,
  HeaderOptionType,
  LanguageType,
  PaymentElementType,
  PositionSummarizeDefault,
  SettingAreaPunchingType,
  SettingCertifiedDate,
} from "@/enums/enums";
import { GetMerchantAddressModelDefault } from "@/helper/addressHelper";
import { GetCredential, GetDefaultPaymentMethodChannel, GetDocumentData } from "@/helper/documentFacadeHelper";
import { DefaultPaymentMethodChannelModel, DocumentDetailModel, OnlineViewModel } from "@/model/document/documentModel";
import { SettingDocumentTemplateModel } from "@/model/document/settingDocumentTemplate";

export function ConvertToExpenseOnlineView(detail: any): OnlineViewModel {
  const onlineViewModel: OnlineViewModel = new OnlineViewModel();
  if (detail) {
    const documentDetailModel: DocumentDetailModel = new DocumentDetailModel();
    documentDetailModel.version = detail.document.version;
    documentDetailModel.data = GetDocumentData(detail.document.data);
    documentDetailModel.defaultPaymentMethodChannel = detail.document.data.contact.paymentMethod
      ? GetDefaultPaymentMethodChannel({ channelList: [detail.document.data.contact.paymentMethod] })
      : new DefaultPaymentMethodChannelModel();
    onlineViewModel.document = documentDetailModel;
    onlineViewModel.transactionId = detail.transactionId;
    onlineViewModel.transactionType = detail.transactionType;
    onlineViewModel.financeType = detail.financeType;
    onlineViewModel.expenseType = detail.expenseType;
    onlineViewModel.template = detail.template
      ? detail.template.data
      : GetExpenseSettingDocument(
          onlineViewModel.expenseType,
          detail.headerOptionType,
          detail.document.data.currency.toCurrency.currencyCode
        );
    onlineViewModel.credential = GetCredential(detail.credential);
    onlineViewModel.headerOptionType = detail.headerOptionType;
    onlineViewModel.isMainTemplate = detail.template?.isMainTemplate ?? false;
    onlineViewModel.doesHaveOwnTemplate = Boolean(detail.template?.data);
  }
  return onlineViewModel;
}

export function GetExpenseSettingDocument(
  expenseType: ExpenseType,
  headerOptionType: ExpenseHeaderType,
  toCurrency: CurrencyCode
): SettingDocumentTemplateModel {
  let settingTemplate = new SettingDocumentTemplateModel();
  switch (headerOptionType) {
    case ExpenseHeaderType.MainExpenseHeader: {
      settingTemplate = {
        colorCode: expenseType === ExpenseType.Expense ? "#33355A" : "#FFA350",
        colorCodeCopy: expenseType === ExpenseType.Expense ? "#33355A" : "#FFA350",
        isShowColorBackground: true,
        isShowColorBorder: false,
        isShowColorBackgroundCopy: true,
        isShowColorBorderCopy: false,
        language: LanguageType.Th,
        areaPunchingType: SettingAreaPunchingType.None,
        isShowWatermarkPaid: false,
        documentFontType: DocumentFontType.Sukhumvit,
        documentFormat: DocumentFormat.FormatOne,
        settingCurrency: toCurrency,
        dateOptionType: DateOptionType.DateADType1,
        documentControlNo: {
          value: "",
          alignment: AlignmentDocumentControlNo.LeftFooter,
        },
        merchantLogo: {
          value: "",
          isShowElement: true,
        },
        isShowEtaxInvoice: true,
        headType: {
          copyAmount: 0,
          originalAmount: 1,
          headerTitleType: HeaderOptionType.Original,
          isShowDocumentSet: false,
          isShowHeaderOptionType: true,
        },
        documentName: {
          headerOptionType: HeaderOptionName.None,
          documentHeaderSize: DocumentHeaderSize.Medium,
          isShowElement: true,
          value: expenseType === ExpenseType.Expense ? "Expense" : "PurchaseInventory",
          isShowTaxInvoice: true,
          isShowNonTaxInvoice: true,
        },
        merchant: {
          isShowTextHeadOffice: true,
          merchantAddressTemplate: GetMerchantAddressModelDefault(),
          isCustomTextDisplay: false,
          isShowClassification: false,
          merchantClassificationList: [],
          merchantTitle: {
            value: "Buyer",
            isShowElement: true,
          },
          phoneNumber: {
            value: "",
            isShowElement: true,
          },
          email: {
            value: "",
            isShowElement: true,
          },
          website: {
            value: "",
            isShowElement: true,
          },
        },
        contact: {
          isShowContact: true,
          isCustomTextDisplay: false,
          isShowTextHeadOffice: true,
          isShowClassification: false,
          contactClassificationList: [],
          contactTitle: {
            value: "Seller",
            isShowElement: true,
          },
          isShowContactCode: true,
          dearTitle: {
            value: "Dear",
            isShowElement: true,
          },
          phoneNumber: {
            value: "",
            isShowElement: true,
          },
          email: {
            value: "",
            isShowElement: true,
          },
          website: {
            value: "",
            isShowElement: true,
          },
        },
        documentInfo: {
          documentNoTitle: {
            value: "Doc_No",
            isShowElement: true,
          },
          issedDateTitle: {
            value: "Issue_Date",
            isShowElement: true,
          },
          creditTitle: {
            value: "Credit_text",
            isShowElement: true,
          },
          acceptedDateTitle: {
            value: "Accect_Date",
            isShowElement: false,
          },
          rejectedDateTitle: {
            value: "Rejected_Date",
            isShowElement: true,
          },
          dueDateTitle: {
            value: "Due_Date",
            isShowElement: true,
          },
          refTitle: {
            value: "Ref",
            isShowElement: true,
          },
          taxInvoiceNumberTitle: {
            value: "TaxInvoice_No",
            isShowElement: true,
          },
          taxInvoiceDateTimeTitle: {
            value: "TaxInvoice_Date",
            isShowElement: true,
          },
          dateOptionType: DateOptionType.DateADType1,
          isCustomTextDisplay: false,
          isShowIssueDateValue: true,
          isShowClassification: false,
          documentInfoClassificationList: [],
        },
        merchantPerson: {
          isShowMerchantPerson: true,
          isCustomTextDisplay: false,
          merchantTitle: {
            value: "",
            isShowElement: true,
          },
          merchantPersonTitle: {
            value: "Contact_Back_At",
            isShowElement: true,
          },
          phoneNumber: {
            value: "",
            isShowElement: true,
          },
          email: {
            value: "",
            isShowElement: true,
          },
        },
        settingColumn: {
          isShowAmount: true,
          isShowUnit: false,
          isShowVatAmount: true,
          isShowPreVatAmount: true,
          isShowIncVatAmount: false,
          isShowWhtAmount: false,
          isShowBilledAmount: false,
          isShowDocumentNumber: false,
          isShowDueDate: false,
          isShowIssedDate: false,
          isShowDiscount: true,
          isShowPrice: true,
          isShowProductItems: false,
          isShowTaxInvoiceNo: true,
        },
        settingDescription: {
          isShowCode: true,
          isShowDescription: true,
          isShowImage: true,
          isShowName: true,
        },
        amountInfo: {
          amountInfoSettingType: AmountInfoSettingType.NotDisplayCaseZero,
          isCustomTextDisplay: false,
          totalBeforeDeductTitle: {
            value: "Total_Value_Before_Deduct",
            isShowElement: false,
          },
          totalAfterDeductTitle: {
            value: "Total_Value_After_Deduct",
            isShowElement: false,
          },
          depositTitle: {
            value: "Deposit_Deduct",
            isShowElement: true,
          },
          discountTitle: {
            value: "Total_Discount",
            isShowElement: true,
          },
          netAmountTitle: {
            value: "Total_Amount",
            isShowElement: true,
          },
          nonVATAmountTitle: {
            value: "VAT_Exempted_Amount",
            isShowElement: true,
          },
          preVATAmountTitle: {
            value: "VAT_7_Amount",
            isShowElement: true,
          },
          vatAmountTitle: {
            value: "VAT_Amount",
            isShowElement: true,
          },
          zeroVATAmountTitle: {
            value: "VAT_0_Amount",
            isShowElement: true,
          },
          totalDocumentAmountAfterTitle: {
            value: "New_amount",
            isShowElement: false,
          },
          referenceTransactionAmountTitle: {
            value: "Total_Amount_Tax_invoice",
            isShowElement: false,
          },
        },
        summarizeInfo: {
          isShowSummary: true,
          isCustomTextDisplay: false,
          positionSummarizeDefault: PositionSummarizeDefault.SummaryAmount,
          summaryAmountTitle: {
            value: "Total_Amount",
            isShowElement: true,
          },
          paymentAmountTitle: {
            value: "Payment_Amount_Expense",
            isShowElement: true,
          },
          whtAmountTitle: {
            value: "WHT_Amount",
            isShowElement: true,
          },
        },
        merchantPayment: {
          paymentElementType: PaymentElementType.Payment,
          settingPaymentGroupList: {
            isShowPaymentGroupList: true,
            isShowPaymentGroupAll: false,
            isShowBankAccountBranchName: true,
            isShowBankAccountName: true,
            isShowBankAccountNumber: true,
            isShowWhtRefNumber: true,
          },
          settingPaymentMethodChannel: {
            isShowBankAccountBranchCode: true,
            isShowBankAccountBranchName: true,
            isShowBankAccountName: true,
            isShowBankAccountNumber: true,
          },
        },
        remark: {
          value: "",
          isShowElement: true,
        },
        reason: {
          value: "",
          isShowElement: false,
        },
        expenseClaimDetail: {
          idCard: {
            value: "",
            isShowElement: false,
          },
          expenseClaimNote: {
            value: "",
            isShowElement: false,
          },
        },
        certified: {
          userCreateTitle: {
            value: "Created_By_Buyer",
            isShowElement: true,
          },
          userApproveTitle: {
            value: "Approved_By_Buyer",
            isShowElement: true,
          },
          merchantStampTitle: {
            value: "Stamp_Buyer",
            isShowElement: true,
          },
          receivedByTitle: {
            value: "Received_By_Seller",
            isShowElement: true,
          },
          stampTitle: {
            value: "Stamp_Seller",
            isShowElement: true,
          },
          userAcceptTitle: {
            value: "Accepted_By",
            isShowElement: false,
          },
          isShowUserAcceptUrl: false,
          isShowUserAcceptDate: false,
          isShowUserAcceptName: false,
          isShowUserCreateUrl: true,
          isShowUserApproveUrl: true,
          isShowUserCreateDate: true,
          isShowUserApproveDate: true,
          isShowUserApproveName: true,
          isShowUserCreateName: true,
          isShowUserCustomerName: true,
          isShowMerchantStampUrl: true,
          isCustomTextDisplay: false,
          isShowQRCode: true,
          settingCreateDate: SettingCertifiedDate.ByDefaultDate,
          settingApproveDate: SettingCertifiedDate.ByDefaultDate,
          settingAcceptDate: SettingCertifiedDate.ByDefaultDate,
        },
      };
      break;
    }
    case ExpenseHeaderType.CustomizeHeader: {
      settingTemplate = {
        colorCode: expenseType === ExpenseType.Expense ? "#33355A" : "#FFA350",
        colorCodeCopy: expenseType === ExpenseType.Expense ? "#33355A" : "#FFA350",
        isShowColorBackground: true,
        isShowColorBorder: false,
        isShowColorBackgroundCopy: true,
        isShowColorBorderCopy: false,
        language: LanguageType.Th,
        areaPunchingType: SettingAreaPunchingType.None,
        isShowWatermarkPaid: false,
        documentFontType: DocumentFontType.Sukhumvit,
        documentFormat: DocumentFormat.FormatOne,
        settingCurrency: toCurrency,
        dateOptionType: DateOptionType.DateADType1,
        documentControlNo: {
          value: "",
          alignment: AlignmentDocumentControlNo.LeftFooter,
        },
        merchantLogo: {
          value: "",
          isShowElement: true,
        },
        isShowEtaxInvoice: true,
        headType: {
          copyAmount: 0,
          originalAmount: 1,
          headerTitleType: HeaderOptionType.Original,
          isShowDocumentSet: false,
          isShowHeaderOptionType: true,
        },
        documentName: {
          headerOptionType: HeaderOptionName.None,
          documentHeaderSize: DocumentHeaderSize.Medium,
          isShowElement: true,
          value: expenseType === ExpenseType.Expense ? "Expense" : "PurchaseInventory",
          isShowTaxInvoice: true,
          isShowNonTaxInvoice: true,
        },
        merchant: {
          isShowTextHeadOffice: true,
          merchantAddressTemplate: GetMerchantAddressModelDefault(),
          isCustomTextDisplay: false,
          isShowClassification: false,
          merchantClassificationList: [],
          merchantTitle: {
            value: "Buyer",
            isShowElement: true,
          },
          phoneNumber: {
            value: "",
            isShowElement: true,
          },
          email: {
            value: "",
            isShowElement: true,
          },
          website: {
            value: "",
            isShowElement: true,
          },
        },
        contact: {
          isShowContact: true,
          isCustomTextDisplay: false,
          isShowTextHeadOffice: true,
          isShowClassification: false,
          contactClassificationList: [],
          contactTitle: {
            value: "Seller",
            isShowElement: true,
          },
          isShowContactCode: true,
          dearTitle: {
            value: "Dear",
            isShowElement: true,
          },
          phoneNumber: {
            value: "",
            isShowElement: true,
          },
          email: {
            value: "",
            isShowElement: true,
          },
          website: {
            value: "",
            isShowElement: true,
          },
        },
        documentInfo: {
          documentNoTitle: {
            value: "Doc_No",
            isShowElement: true,
          },
          issedDateTitle: {
            value: "Issue_Date",
            isShowElement: true,
          },
          creditTitle: {
            value: "Credit_text",
            isShowElement: true,
          },
          acceptedDateTitle: {
            value: "Accect_Date",
            isShowElement: false,
          },
          rejectedDateTitle: {
            value: "Rejected_Date",
            isShowElement: true,
          },
          dueDateTitle: {
            value: "Due_Date",
            isShowElement: true,
          },
          refTitle: {
            value: "Ref",
            isShowElement: true,
          },
          taxInvoiceNumberTitle: {
            value: "TaxInvoice_No",
            isShowElement: true,
          },
          taxInvoiceDateTimeTitle: {
            value: "TaxInvoice_Date",
            isShowElement: true,
          },
          dateOptionType: DateOptionType.DateADType1,
          isCustomTextDisplay: false,
          isShowIssueDateValue: true,
          isShowClassification: false,
          documentInfoClassificationList: [],
        },
        merchantPerson: {
          isShowMerchantPerson: true,
          isCustomTextDisplay: false,
          merchantTitle: {
            value: "",
            isShowElement: true,
          },
          merchantPersonTitle: {
            value: "Contact_Back_At",
            isShowElement: true,
          },
          phoneNumber: {
            value: "",
            isShowElement: true,
          },
          email: {
            value: "",
            isShowElement: true,
          },
        },
        settingColumn: {
          isShowAmount: true,
          isShowUnit: false,
          isShowVatAmount: true,
          isShowPreVatAmount: true,
          isShowIncVatAmount: false,
          isShowWhtAmount: false,
          isShowBilledAmount: false,
          isShowDocumentNumber: false,
          isShowDueDate: false,
          isShowIssedDate: false,
          isShowDiscount: true,
          isShowPrice: true,
          isShowProductItems: false,
          isShowTaxInvoiceNo: true,
        },
        settingDescription: {
          isShowCode: true,
          isShowDescription: true,
          isShowImage: true,
          isShowName: true,
        },
        amountInfo: {
          amountInfoSettingType: AmountInfoSettingType.NotDisplayCaseZero,
          isCustomTextDisplay: false,
          totalBeforeDeductTitle: {
            value: "Total_Value_Before_Deduct",
            isShowElement: false,
          },
          totalAfterDeductTitle: {
            value: "Total_Value_After_Deduct",
            isShowElement: false,
          },
          depositTitle: {
            value: "Deposit_Deduct",
            isShowElement: true,
          },
          discountTitle: {
            value: "Total_Discount",
            isShowElement: true,
          },
          netAmountTitle: {
            value: "Total_Amount",
            isShowElement: true,
          },
          nonVATAmountTitle: {
            value: "VAT_Exempted_Amount",
            isShowElement: true,
          },
          preVATAmountTitle: {
            value: "VAT_7_Amount",
            isShowElement: true,
          },
          vatAmountTitle: {
            value: "VAT_Amount",
            isShowElement: true,
          },
          zeroVATAmountTitle: {
            value: "VAT_0_Amount",
            isShowElement: true,
          },
          totalDocumentAmountAfterTitle: {
            value: "New_amount",
            isShowElement: false,
          },
          referenceTransactionAmountTitle: {
            value: "Total_Amount_Tax_invoice",
            isShowElement: false,
          },
        },
        summarizeInfo: {
          isShowSummary: true,
          isCustomTextDisplay: false,
          positionSummarizeDefault: PositionSummarizeDefault.SummaryAmount,
          summaryAmountTitle: {
            value: "Total_Amount",
            isShowElement: true,
          },
          paymentAmountTitle: {
            value: "Payment_Amount_Expense",
            isShowElement: true,
          },
          whtAmountTitle: {
            value: "WHT_Amount",
            isShowElement: true,
          },
        },
        merchantPayment: {
          paymentElementType: PaymentElementType.Payment,
          settingPaymentGroupList: {
            isShowPaymentGroupList: true,
            isShowPaymentGroupAll: false,
            isShowBankAccountBranchName: true,
            isShowBankAccountName: true,
            isShowBankAccountNumber: true,
            isShowWhtRefNumber: true,
          },
          settingPaymentMethodChannel: {
            isShowBankAccountBranchCode: true,
            isShowBankAccountBranchName: true,
            isShowBankAccountName: true,
            isShowBankAccountNumber: true,
          },
        },
        remark: {
          value: "",
          isShowElement: true,
        },
        reason: {
          value: "",
          isShowElement: false,
        },
        expenseClaimDetail: {
          idCard: {
            value: "",
            isShowElement: false,
          },
          expenseClaimNote: {
            value: "",
            isShowElement: false,
          },
        },
        certified: {
          userCreateTitle: {
            value: "Created_By_Buyer",
            isShowElement: true,
          },
          userApproveTitle: {
            value: "Approved_By_Buyer",
            isShowElement: true,
          },
          merchantStampTitle: {
            value: "Stamp_Buyer",
            isShowElement: true,
          },
          receivedByTitle: {
            value: "Received_By_Seller",
            isShowElement: true,
          },
          stampTitle: {
            value: "Stamp_Seller",
            isShowElement: true,
          },
          userAcceptTitle: {
            value: "Accepted_By",
            isShowElement: false,
          },
          isShowUserAcceptUrl: false,
          isShowUserAcceptDate: false,
          isShowUserAcceptName: false,
          isShowUserCreateUrl: true,
          isShowUserApproveUrl: true,
          isShowUserCreateDate: true,
          isShowUserApproveDate: true,
          isShowUserApproveName: true,
          isShowUserCreateName: true,
          isShowUserCustomerName: true,
          isShowMerchantStampUrl: true,
          isCustomTextDisplay: false,
          isShowQRCode: true,
          settingCreateDate: SettingCertifiedDate.ByDefaultDate,
          settingApproveDate: SettingCertifiedDate.ByDefaultDate,
          settingAcceptDate: SettingCertifiedDate.ByDefaultDate,
        },
      };
      break;
    }
    case ExpenseHeaderType.PaymentNoteHeader: {
      settingTemplate = {
        colorCode: expenseType === ExpenseType.Expense ? "#33355A" : "#FFA350",
        colorCodeCopy: expenseType === ExpenseType.Expense ? "#33355A" : "#FFA350",
        isShowColorBackground: true,
        isShowColorBorder: false,
        isShowColorBackgroundCopy: true,
        isShowColorBorderCopy: false,
        language: LanguageType.Th,
        areaPunchingType: SettingAreaPunchingType.None,
        isShowWatermarkPaid: false,
        documentFontType: DocumentFontType.Sukhumvit,
        documentFormat: DocumentFormat.FormatOne,
        settingCurrency: toCurrency,
        dateOptionType: DateOptionType.DateADType1,
        documentControlNo: {
          value: "",
          alignment: AlignmentDocumentControlNo.LeftFooter,
        },
        merchantLogo: {
          value: "",
          isShowElement: true,
        },
        isShowEtaxInvoice: true,
        headType: {
          copyAmount: 0,
          originalAmount: 1,
          headerTitleType: HeaderOptionType.Original,
          isShowDocumentSet: false,
          isShowHeaderOptionType: true,
        },
        documentName: {
          headerOptionType: HeaderOptionName.None,
          documentHeaderSize: DocumentHeaderSize.Medium,
          isShowElement: true,
          value: "Payment_Note",
          isShowTaxInvoice: true,
          isShowNonTaxInvoice: true,
        },
        merchant: {
          isShowTextHeadOffice: true,
          merchantAddressTemplate: GetMerchantAddressModelDefault(),
          isCustomTextDisplay: false,
          isShowClassification: false,
          merchantClassificationList: [],
          merchantTitle: {
            value: "Buyer",
            isShowElement: true,
          },
          phoneNumber: {
            value: "",
            isShowElement: true,
          },
          email: {
            value: "",
            isShowElement: true,
          },
          website: {
            value: "",
            isShowElement: true,
          },
        },
        contact: {
          isShowContact: true,
          isCustomTextDisplay: false,
          isShowTextHeadOffice: true,
          isShowClassification: false,
          contactClassificationList: [],
          contactTitle: {
            value: "Seller",
            isShowElement: true,
          },
          isShowContactCode: true,
          dearTitle: {
            value: "Dear",
            isShowElement: true,
          },
          phoneNumber: {
            value: "",
            isShowElement: true,
          },
          email: {
            value: "",
            isShowElement: true,
          },
          website: {
            value: "",
            isShowElement: true,
          },
        },
        documentInfo: {
          documentNoTitle: {
            value: "Doc_No",
            isShowElement: true,
          },
          issedDateTitle: {
            value: "Issue_Date",
            isShowElement: true,
          },
          creditTitle: {
            value: "Credit_text",
            isShowElement: false,
          },
          acceptedDateTitle: {
            value: "Accect_Date",
            isShowElement: false,
          },
          rejectedDateTitle: {
            value: "Rejected_Date",
            isShowElement: true,
          },
          dueDateTitle: {
            value: "Due_Date",
            isShowElement: false,
          },
          refTitle: {
            value: "Ref",
            isShowElement: true,
          },
          taxInvoiceNumberTitle: {
            value: "TaxInvoice_No",
            isShowElement: false,
          },
          taxInvoiceDateTimeTitle: {
            value: "TaxInvoice_Date",
            isShowElement: false,
          },
          dateOptionType: DateOptionType.DateADType1,
          isCustomTextDisplay: false,
          isShowIssueDateValue: true,
          isShowClassification: false,
          documentInfoClassificationList: [],
        },
        merchantPerson: {
          isShowMerchantPerson: true,
          isCustomTextDisplay: false,
          merchantTitle: {
            value: "",
            isShowElement: true,
          },
          merchantPersonTitle: {
            value: "Contact_Back_At",
            isShowElement: true,
          },
          phoneNumber: {
            value: "",
            isShowElement: true,
          },
          email: {
            value: "",
            isShowElement: true,
          },
        },
        settingColumn: {
          isShowAmount: true,
          isShowUnit: false,
          isShowVatAmount: true,
          isShowPreVatAmount: true,
          isShowIncVatAmount: false,
          isShowWhtAmount: false,
          isShowBilledAmount: false,
          isShowDocumentNumber: false,
          isShowDueDate: false,
          isShowIssedDate: false,
          isShowDiscount: true,
          isShowPrice: true,
          isShowProductItems: false,
          isShowTaxInvoiceNo: true,
        },
        settingDescription: {
          isShowCode: true,
          isShowDescription: true,
          isShowImage: true,
          isShowName: true,
        },
        amountInfo: {
          amountInfoSettingType: AmountInfoSettingType.NotDisplayCaseZero,
          isCustomTextDisplay: false,
          totalBeforeDeductTitle: {
            value: "Total_Value_Before_Deduct",
            isShowElement: false,
          },
          totalAfterDeductTitle: {
            value: "Total_Value_After_Deduct",
            isShowElement: false,
          },
          depositTitle: {
            value: "Deposit_Deduct",
            isShowElement: true,
          },
          discountTitle: {
            value: "Total_Discount",
            isShowElement: true,
          },
          netAmountTitle: {
            value: "Total_Amount",
            isShowElement: true,
          },
          nonVATAmountTitle: {
            value: "VAT_Exempted_Amount",
            isShowElement: true,
          },
          preVATAmountTitle: {
            value: "VAT_7_Amount",
            isShowElement: true,
          },
          vatAmountTitle: {
            value: "VAT_Amount",
            isShowElement: true,
          },
          zeroVATAmountTitle: {
            value: "VAT_0_Amount",
            isShowElement: true,
          },
          totalDocumentAmountAfterTitle: {
            value: "New_amount",
            isShowElement: false,
          },
          referenceTransactionAmountTitle: {
            value: "Total_Amount_Tax_invoice",
            isShowElement: false,
          },
        },
        summarizeInfo: {
          isShowSummary: true,
          isCustomTextDisplay: false,
          positionSummarizeDefault: PositionSummarizeDefault.SummaryAmount,
          summaryAmountTitle: {
            value: "Total_Amount",
            isShowElement: true,
          },
          paymentAmountTitle: {
            value: "Payment_Amount_Expense",
            isShowElement: true,
          },
          whtAmountTitle: {
            value: "WHT_Amount",
            isShowElement: true,
          },
        },
        merchantPayment: {
          paymentElementType: PaymentElementType.List,
          settingPaymentGroupList: {
            isShowPaymentGroupList: true,
            isShowPaymentGroupAll: false,
            isShowBankAccountBranchName: true,
            isShowBankAccountName: true,
            isShowBankAccountNumber: true,
            isShowWhtRefNumber: true,
          },
          settingPaymentMethodChannel: {
            isShowBankAccountBranchCode: true,
            isShowBankAccountBranchName: true,
            isShowBankAccountName: true,
            isShowBankAccountNumber: true,
          },
        },
        remark: {
          value: "",
          isShowElement: true,
        },
        reason: {
          value: "",
          isShowElement: false,
        },
        expenseClaimDetail: {
          idCard: {
            value: "",
            isShowElement: false,
          },
          expenseClaimNote: {
            value: "",
            isShowElement: false,
          },
        },
        certified: {
          userCreateTitle: {
            value: "Created_By_Buyer",
            isShowElement: true,
          },
          userApproveTitle: {
            value: "Approved_By_Buyer",
            isShowElement: true,
          },
          merchantStampTitle: {
            value: "Stamp_Buyer",
            isShowElement: true,
          },
          receivedByTitle: {
            value: "Received_By_Seller",
            isShowElement: false,
          },
          stampTitle: {
            value: "Stamp_Seller",
            isShowElement: false,
          },
          userAcceptTitle: {
            value: "Accepted_By",
            isShowElement: false,
          },
          isShowUserAcceptUrl: false,
          isShowUserAcceptDate: false,
          isShowUserAcceptName: false,
          isShowUserCreateUrl: true,
          isShowUserApproveUrl: true,
          isShowUserCreateDate: true,
          isShowUserApproveDate: true,
          isShowUserApproveName: true,
          isShowUserCreateName: true,
          isShowUserCustomerName: true,
          isShowMerchantStampUrl: true,
          isCustomTextDisplay: false,
          isShowQRCode: true,
          settingCreateDate: SettingCertifiedDate.ByDefaultDate,
          settingApproveDate: SettingCertifiedDate.ByDefaultDate,
          settingAcceptDate: SettingCertifiedDate.ByDefaultDate,
        },
      };
      break;
    }
    case ExpenseHeaderType.ClaimNoteHeader: {
      settingTemplate = {
        colorCode: expenseType === ExpenseType.Expense ? "#33355A" : "#FFA350",
        colorCodeCopy: expenseType === ExpenseType.Expense ? "#33355A" : "#FFA350",
        isShowColorBackground: true,
        isShowColorBorder: false,
        isShowColorBackgroundCopy: true,
        isShowColorBorderCopy: false,
        language: LanguageType.Th,
        areaPunchingType: SettingAreaPunchingType.None,
        isShowWatermarkPaid: false,
        documentFontType: DocumentFontType.Sukhumvit,
        documentFormat: DocumentFormat.FormatOne,
        settingCurrency: toCurrency,
        dateOptionType: DateOptionType.DateADType1,
        documentControlNo: {
          value: "",
          alignment: AlignmentDocumentControlNo.LeftFooter,
        },
        merchantLogo: {
          value: "",
          isShowElement: true,
        },
        isShowEtaxInvoice: true,
        headType: {
          copyAmount: 0,
          originalAmount: 1,
          headerTitleType: HeaderOptionType.Original,
          isShowDocumentSet: false,
          isShowHeaderOptionType: true,
        },
        documentName: {
          headerOptionType: HeaderOptionName.None,
          documentHeaderSize: DocumentHeaderSize.Medium,
          isShowElement: true,
          value: "Expense_Claim_Note",
          isShowTaxInvoice: true,
          isShowNonTaxInvoice: true,
        },
        merchant: {
          isShowTextHeadOffice: true,
          merchantAddressTemplate: GetMerchantAddressModelDefault(),
          isCustomTextDisplay: false,
          isShowClassification: false,
          merchantClassificationList: [],
          merchantTitle: {
            value: "Buyer",
            isShowElement: true,
          },
          phoneNumber: {
            value: "",
            isShowElement: true,
          },
          email: {
            value: "",
            isShowElement: true,
          },
          website: {
            value: "",
            isShowElement: true,
          },
        },
        contact: {
          isShowContact: false,
          isCustomTextDisplay: false,
          isShowContactCode: true,
          isShowTextHeadOffice: true,
          isShowClassification: false,
          contactClassificationList: [],
          contactTitle: {
            value: "Seller",
            isShowElement: true,
          },
          dearTitle: {
            value: "Dear",
            isShowElement: true,
          },
          phoneNumber: {
            value: "",
            isShowElement: true,
          },
          email: {
            value: "",
            isShowElement: true,
          },
          website: {
            value: "",
            isShowElement: true,
          },
        },
        documentInfo: {
          documentNoTitle: {
            value: "Doc_No",
            isShowElement: true,
          },
          issedDateTitle: {
            value: "Issue_Date",
            isShowElement: true,
          },
          creditTitle: {
            value: "Credit_text",
            isShowElement: false,
          },
          acceptedDateTitle: {
            value: "Accect_Date",
            isShowElement: false,
          },
          rejectedDateTitle: {
            value: "Rejected_Date",
            isShowElement: true,
          },
          dueDateTitle: {
            value: "Due_Date",
            isShowElement: false,
          },
          refTitle: {
            value: "Ref",
            isShowElement: true,
          },
          taxInvoiceNumberTitle: {
            value: "TaxInvoice_No",
            isShowElement: false,
          },
          taxInvoiceDateTimeTitle: {
            value: "TaxInvoice_Date",
            isShowElement: false,
          },
          dateOptionType: DateOptionType.DateADType1,
          isCustomTextDisplay: false,
          isShowIssueDateValue: true,
          isShowClassification: false,
          documentInfoClassificationList: [],
        },
        merchantPerson: {
          isShowMerchantPerson: false,
          isCustomTextDisplay: false,
          merchantTitle: {
            value: "",
            isShowElement: true,
          },
          merchantPersonTitle: {
            value: "Contact_Back_At",
            isShowElement: false,
          },
          phoneNumber: {
            value: "",
            isShowElement: false,
          },
          email: {
            value: "",
            isShowElement: false,
          },
        },
        settingColumn: {
          isShowAmount: true,
          isShowUnit: false,
          isShowVatAmount: true,
          isShowPreVatAmount: true,
          isShowIncVatAmount: false,
          isShowWhtAmount: false,
          isShowBilledAmount: false,
          isShowDocumentNumber: false,
          isShowDueDate: false,
          isShowIssedDate: false,
          isShowDiscount: true,
          isShowPrice: true,
          isShowProductItems: false,
          isShowTaxInvoiceNo: true,
        },
        settingDescription: {
          isShowCode: true,
          isShowDescription: true,
          isShowImage: true,
          isShowName: true,
        },
        amountInfo: {
          amountInfoSettingType: AmountInfoSettingType.NotDisplayCaseZero,
          isCustomTextDisplay: false,
          totalBeforeDeductTitle: {
            value: "Total_Value_Before_Deduct",
            isShowElement: false,
          },
          totalAfterDeductTitle: {
            value: "Total_Value_After_Deduct",
            isShowElement: false,
          },
          depositTitle: {
            value: "Deposit_Deduct",
            isShowElement: true,
          },
          discountTitle: {
            value: "Total_Discount",
            isShowElement: true,
          },
          netAmountTitle: {
            value: "Total_Amount",
            isShowElement: true,
          },
          nonVATAmountTitle: {
            value: "VAT_Exempted_Amount",
            isShowElement: true,
          },
          preVATAmountTitle: {
            value: "VAT_7_Amount",
            isShowElement: true,
          },
          vatAmountTitle: {
            value: "VAT_Amount",
            isShowElement: true,
          },
          zeroVATAmountTitle: {
            value: "VAT_0_Amount",
            isShowElement: true,
          },
          totalDocumentAmountAfterTitle: {
            value: "New_amount",
            isShowElement: false,
          },
          referenceTransactionAmountTitle: {
            value: "Total_Amount_Tax_invoice",
            isShowElement: false,
          },
        },
        summarizeInfo: {
          isShowSummary: true,
          isCustomTextDisplay: false,
          positionSummarizeDefault: PositionSummarizeDefault.SummaryAmount,
          summaryAmountTitle: {
            value: "Total_Amount",
            isShowElement: true,
          },
          paymentAmountTitle: {
            value: "Payment_Amount_Expense",
            isShowElement: true,
          },
          whtAmountTitle: {
            value: "WHT_Amount",
            isShowElement: true,
          },
        },
        merchantPayment: {
          paymentElementType: PaymentElementType.Payment,
          settingPaymentGroupList: {
            isShowPaymentGroupList: true,
            isShowPaymentGroupAll: false,
            isShowBankAccountBranchName: true,
            isShowBankAccountName: true,
            isShowBankAccountNumber: true,
            isShowWhtRefNumber: true,
          },
          settingPaymentMethodChannel: {
            isShowBankAccountBranchCode: true,
            isShowBankAccountBranchName: true,
            isShowBankAccountName: true,
            isShowBankAccountNumber: true,
          },
        },
        remark: {
          value: "",
          isShowElement: true,
        },
        reason: {
          value: "",
          isShowElement: false,
        },
        expenseClaimDetail: {
          idCard: {
            value: "",
            isShowElement: false,
          },
          expenseClaimNote: {
            value: "",
            isShowElement: true,
          },
        },
        certified: {
          userCreateTitle: {
            value: "Payer",
            isShowElement: true,
          },
          userApproveTitle: {
            value: "Approver",
            isShowElement: true,
          },
          merchantStampTitle: {
            value: "Stamp_Merchant",
            isShowElement: true,
          },
          receivedByTitle: {
            value: "Received_By_Seller",
            isShowElement: false,
          },
          stampTitle: {
            value: "Stamp_Seller",
            isShowElement: false,
          },
          userAcceptTitle: {
            value: "Accepted_By",
            isShowElement: false,
          },
          isShowUserAcceptUrl: false,
          isShowUserAcceptDate: false,
          isShowUserAcceptName: false,
          isShowUserCreateUrl: true,
          isShowUserApproveUrl: true,
          isShowUserCreateDate: true,
          isShowUserApproveDate: true,
          isShowUserApproveName: true,
          isShowUserCreateName: true,
          isShowUserCustomerName: true,
          isShowMerchantStampUrl: true,
          isCustomTextDisplay: false,
          isShowQRCode: true,
          settingCreateDate: SettingCertifiedDate.ByDefaultDate,
          settingApproveDate: SettingCertifiedDate.ByDefaultDate,
          settingAcceptDate: SettingCertifiedDate.ByDefaultDate,
        },
      };
      break;
    }
    case ExpenseHeaderType.BillOfLadingHeader: {
      settingTemplate = {
        colorCode: expenseType === ExpenseType.Expense ? "#33355A" : "#FFA350",
        colorCodeCopy: expenseType === ExpenseType.Expense ? "#33355A" : "#FFA350",
        isShowColorBackground: true,
        isShowColorBorder: false,
        isShowColorBackgroundCopy: true,
        isShowColorBorderCopy: false,
        language: LanguageType.Th,
        areaPunchingType: SettingAreaPunchingType.None,
        isShowWatermarkPaid: false,
        documentFontType: DocumentFontType.Sukhumvit,
        documentFormat: DocumentFormat.FormatOne,
        settingCurrency: toCurrency,
        dateOptionType: DateOptionType.DateADType1,
        documentControlNo: {
          value: "",
          alignment: AlignmentDocumentControlNo.LeftFooter,
        },
        merchantLogo: {
          value: "",
          isShowElement: true,
        },
        isShowEtaxInvoice: true,
        headType: {
          copyAmount: 0,
          originalAmount: 1,
          headerTitleType: HeaderOptionType.Original,
          isShowDocumentSet: false,
          isShowHeaderOptionType: true,
        },
        documentName: {
          headerOptionType: HeaderOptionName.None,
          documentHeaderSize: DocumentHeaderSize.Medium,
          isShowElement: true,
          value: "Bill_Lading",
          isShowTaxInvoice: true,
          isShowNonTaxInvoice: true,
        },
        merchant: {
          isShowTextHeadOffice: true,
          merchantAddressTemplate: GetMerchantAddressModelDefault(),
          isCustomTextDisplay: false,
          isShowClassification: false,
          merchantClassificationList: [],
          merchantTitle: {
            value: "Buyer",
            isShowElement: true,
          },
          phoneNumber: {
            value: "",
            isShowElement: true,
          },
          email: {
            value: "",
            isShowElement: true,
          },
          website: {
            value: "",
            isShowElement: true,
          },
        },
        contact: {
          isShowContact: true,
          isCustomTextDisplay: false,
          isShowTextHeadOffice: true,
          isShowClassification: false,
          contactClassificationList: [],
          contactTitle: {
            value: "Seller",
            isShowElement: true,
          },
          isShowContactCode: true,
          dearTitle: {
            value: "Dear",
            isShowElement: true,
          },
          phoneNumber: {
            value: "",
            isShowElement: true,
          },
          email: {
            value: "",
            isShowElement: true,
          },
          website: {
            value: "",
            isShowElement: true,
          },
        },
        documentInfo: {
          documentNoTitle: {
            value: "Doc_No",
            isShowElement: true,
          },
          issedDateTitle: {
            value: "Issue_Date",
            isShowElement: true,
          },
          creditTitle: {
            value: "Credit_text",
            isShowElement: true,
          },
          acceptedDateTitle: {
            value: "Accect_Date",
            isShowElement: false,
          },
          rejectedDateTitle: {
            value: "Rejected_Date",
            isShowElement: true,
          },
          dueDateTitle: {
            value: "Due_Date",
            isShowElement: true,
          },
          refTitle: {
            value: "Ref",
            isShowElement: true,
          },
          taxInvoiceNumberTitle: {
            value: "TaxInvoice_No",
            isShowElement: true,
          },
          taxInvoiceDateTimeTitle: {
            value: "TaxInvoice_Date",
            isShowElement: true,
          },
          dateOptionType: DateOptionType.DateADType1,
          isCustomTextDisplay: false,
          isShowIssueDateValue: true,
          isShowClassification: false,
          documentInfoClassificationList: [],
        },
        merchantPerson: {
          isShowMerchantPerson: true,
          isCustomTextDisplay: false,
          merchantTitle: {
            value: "",
            isShowElement: true,
          },
          merchantPersonTitle: {
            value: "Contact_Back_At",
            isShowElement: true,
          },
          phoneNumber: {
            value: "",
            isShowElement: true,
          },
          email: {
            value: "",
            isShowElement: true,
          },
        },
        settingColumn: {
          isShowAmount: true,
          isShowUnit: false,
          isShowVatAmount: true,
          isShowPreVatAmount: true,
          isShowIncVatAmount: false,
          isShowWhtAmount: false,
          isShowBilledAmount: false,
          isShowDocumentNumber: false,
          isShowDueDate: false,
          isShowIssedDate: false,
          isShowDiscount: true,
          isShowPrice: true,
          isShowProductItems: false,
          isShowTaxInvoiceNo: true,
        },
        settingDescription: {
          isShowCode: true,
          isShowDescription: true,
          isShowImage: true,
          isShowName: true,
        },
        amountInfo: {
          amountInfoSettingType: AmountInfoSettingType.NotDisplayCaseZero,
          isCustomTextDisplay: false,
          totalBeforeDeductTitle: {
            value: "Total_Value_Before_Deduct",
            isShowElement: false,
          },
          totalAfterDeductTitle: {
            value: "Total_Value_After_Deduct",
            isShowElement: false,
          },
          depositTitle: {
            value: "Deposit_Deduct",
            isShowElement: true,
          },
          discountTitle: {
            value: "Total_Discount",
            isShowElement: true,
          },
          netAmountTitle: {
            value: "Total_Amount",
            isShowElement: true,
          },
          nonVATAmountTitle: {
            value: "VAT_Exempted_Amount",
            isShowElement: true,
          },
          preVATAmountTitle: {
            value: "VAT_7_Amount",
            isShowElement: true,
          },
          vatAmountTitle: {
            value: "VAT_Amount",
            isShowElement: true,
          },
          zeroVATAmountTitle: {
            value: "VAT_0_Amount",
            isShowElement: true,
          },
          totalDocumentAmountAfterTitle: {
            value: "New_amount",
            isShowElement: false,
          },
          referenceTransactionAmountTitle: {
            value: "Total_Amount_Tax_invoice",
            isShowElement: false,
          },
        },
        summarizeInfo: {
          isShowSummary: true,
          isCustomTextDisplay: false,
          positionSummarizeDefault: PositionSummarizeDefault.SummaryAmount,
          summaryAmountTitle: {
            value: "Total_Amount",
            isShowElement: true,
          },
          paymentAmountTitle: {
            value: "Payment_Amount_Expense",
            isShowElement: true,
          },
          whtAmountTitle: {
            value: "WHT_Amount",
            isShowElement: true,
          },
        },
        merchantPayment: {
          paymentElementType: PaymentElementType.Payment,
          settingPaymentGroupList: {
            isShowPaymentGroupList: true,
            isShowPaymentGroupAll: false,
            isShowBankAccountBranchName: true,
            isShowBankAccountName: true,
            isShowBankAccountNumber: true,
            isShowWhtRefNumber: true,
          },
          settingPaymentMethodChannel: {
            isShowBankAccountBranchCode: true,
            isShowBankAccountBranchName: true,
            isShowBankAccountName: true,
            isShowBankAccountNumber: true,
          },
        },
        remark: {
          value: "",
          isShowElement: true,
        },
        reason: {
          value: "",
          isShowElement: false,
        },
        expenseClaimDetail: {
          idCard: {
            value: "",
            isShowElement: false,
          },
          expenseClaimNote: {
            value: "",
            isShowElement: false,
          },
        },
        certified: {
          userCreateTitle: {
            value: "Created_By_Buyer",
            isShowElement: true,
          },
          userApproveTitle: {
            value: "Approved_By_Buyer",
            isShowElement: true,
          },
          merchantStampTitle: {
            value: "Stamp_Buyer",
            isShowElement: true,
          },
          receivedByTitle: {
            value: "Received_By_Seller",
            isShowElement: true,
          },
          stampTitle: {
            value: "Stamp_Seller",
            isShowElement: true,
          },
          userAcceptTitle: {
            value: "Accepted_By",
            isShowElement: false,
          },
          isShowUserAcceptUrl: false,
          isShowUserAcceptDate: false,
          isShowUserAcceptName: false,
          isShowUserCreateUrl: true,
          isShowUserApproveUrl: true,
          isShowUserCreateDate: true,
          isShowUserApproveDate: true,
          isShowUserApproveName: true,
          isShowUserCreateName: true,
          isShowUserCustomerName: true,
          isShowMerchantStampUrl: true,
          isCustomTextDisplay: false,
          isShowQRCode: true,
          settingCreateDate: SettingCertifiedDate.ByDefaultDate,
          settingApproveDate: SettingCertifiedDate.ByDefaultDate,
          settingAcceptDate: SettingCertifiedDate.ByDefaultDate,
        },
      };
      break;
    }
  }

  return settingTemplate;
}
